import { unified } from 'unified';
import rehypeParse from 'rehype-parse';

/**
 * Pre-publish SEO checks for a content piece.
 *
 * Reads the piece's **html**, not its markdown. The html is canonical: it is
 * what the editor saves and where the image round writes its slots, so it is
 * the only copy that reflects the article as it stands right now.
 *
 * Nothing here is stored. The readout is recomputed on every render, because a
 * quality number frozen at build time starts lying the moment someone edits.
 *
 * Deliberately no composite score. The moment there is one number, people
 * optimize to turn it green, which is exactly the failure we avoided by not
 * gating on an AI-content detector. Individual checks carry their reasons.
 */

export type SeoStatus = 'pass' | 'warn' | 'fail';

export interface SeoCheck {
  id: string;
  label: string;
  status: SeoStatus;
  detail: string;
  hint?: string;
}

export interface SeoResult {
  checks: SeoCheck[];
  counts: Record<SeoStatus, number>;
  words: number;
}

export interface SeoOptions {
  primary?: string | null;
  secondary?: string[];
  /** Terms the client's Voice file says never to use for our own service. */
  offVoice?: string[];
  /** Site domain, so absolute self-links still count as internal. */
  domain?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  /** Other pieces on the same client, for near-duplicate detection. */
  library?: { title: string; html: string }[];
  thresholds?: Partial<SeoThresholds>;
}

export interface SeoThresholds {
  minWords: number;
  maxWords: number;
  minDensity: number;
  maxDensity: number;
  ceiling: number;
  minInternalLinks: number;
  minAltWords: number;
  maxQuestionHeadings: number;
  maxExclamations: number;
  maxAvgSentence: number;
  maxParagraph: number;
  maxOverlap: number;
  ticFloor: number;
}

export const SEO_DEFAULTS: SeoThresholds = {
  minWords: 1200,
  maxWords: 5000,
  minDensity: 0.5,
  maxDensity: 1.5,
  ceiling: 2.0,
  minInternalLinks: 3,
  minAltWords: 3,
  maxQuestionHeadings: 40,
  maxExclamations: 0,
  maxAvgSentence: 24,
  maxParagraph: 150,
  maxOverlap: 35,
  ticFloor: 3,
};

/* ---------- html → the pieces we measure ---------- */

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

interface Extract {
  headings: { level: number; text: string }[];
  images: { alt: string; src: string }[];
  links: { text: string; href: string }[];
  paragraphs: string[];
  /** Body prose with headings removed — what density is measured against. */
  body: string;
}

const HEADING = /^h([1-6])$/;
const BLOCK = new Set(['p', 'li', 'blockquote', 'td', 'th', 'figcaption', 'dd', 'dt']);

function textOf(node: HastNode): string {
  if (node.type === 'text') return node.value ?? '';
  if (node.tagName === 'script' || node.tagName === 'style') return '';
  return (node.children ?? []).map(textOf).join('');
}

function extract(html: string): Extract {
  const tree = unified().use(rehypeParse, { fragment: true }).parse(html) as unknown as HastNode;
  const out: Extract = { headings: [], images: [], links: [], paragraphs: [], body: '' };
  const bodyParts: string[] = [];

  const walk = (node: HastNode) => {
    const tag = node.tagName;
    if (tag) {
      const h = HEADING.exec(tag);
      if (h) {
        out.headings.push({ level: Number(h[1]), text: textOf(node).trim() });
        return; // heading text is not body prose
      }
      if (tag === 'img') {
        const p = node.properties ?? {};
        out.images.push({ alt: String(p.alt ?? ''), src: String(p.src ?? '') });
        return;
      }
      if (tag === 'a') {
        const p = node.properties ?? {};
        out.links.push({ text: textOf(node).trim(), href: String(p.href ?? '') });
      }
      if (BLOCK.has(tag)) {
        const t = textOf(node).trim();
        if (t) {
          out.paragraphs.push(t);
          bodyParts.push(t);
        }
        // children already captured as text; links inside were recorded above
        (node.children ?? []).forEach((c) => collectLinks(c, out));
        return;
      }
    }
    (node.children ?? []).forEach(walk);
  };

  walk(tree);
  out.body = bodyParts.join('\n\n');
  return out;
}

/** Links inside a block still need recording once the block short-circuits the walk. */
function collectLinks(node: HastNode, out: Extract) {
  if (node.tagName === 'a') {
    const p = node.properties ?? {};
    out.links.push({ text: textOf(node).trim(), href: String(p.href ?? '') });
  }
  if (node.tagName === 'img') {
    const p = node.properties ?? {};
    out.images.push({ alt: String(p.alt ?? ''), src: String(p.src ?? '') });
  }
  (node.children ?? []).forEach((c) => collectLinks(c, out));
}

/* ---------- text helpers ---------- */

export function words(text: string): string[] {
  return text.toLowerCase().match(/[a-z0-9']+/g) ?? [];
}

/** Naive singular, so "hairline cracks" matches a "hairline crack" target. */
export function stem(w: string): string {
  if (w.length > 3 && w.endsWith('es') && !/(ses|ees)$/.test(w)) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

function stems(text: string): string[] {
  return words(text).map(stem);
}

function sentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => words(s).length >= 3);
}

function syllables(w: string): number {
  const clean = w.replace(/[^a-z]/g, '');
  if (!clean) return 0;
  if (clean.length <= 3) return 1;
  const trimmed = clean.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '');
  return Math.max(1, (trimmed.match(/[aeiouy]{1,2}/g) ?? []).length);
}

function flesch(text: string): number | null {
  const ws = words(text);
  const ss = sentences(text);
  if (!ws.length || !ss.length) return null;
  const syl = ws.reduce((n, w) => n + syllables(w), 0);
  return 206.835 - 1.015 * (ws.length / ss.length) - 84.6 * (syl / ws.length);
}

/** Count of a phrase and its simple plural variants, plus its density. */
export function phraseCount(haystack: string[], phrase: string): { hits: number; density: number } {
  const target = words(phrase).map(stem);
  if (!target.length || !haystack.length) return { hits: 0, density: 0 };
  let hits = 0;
  for (let i = 0; i <= haystack.length - target.length; i += 1) {
    let match = true;
    for (let j = 0; j < target.length; j += 1) {
      if (haystack[i + j] !== target[j]) {
        match = false;
        break;
      }
    }
    if (match) hits += 1;
  }
  return { hits, density: (hits * target.length * 100) / haystack.length };
}

function ngrams(ws: string[], n: number): Map<string, number> {
  const counts = new Map<string, number>();
  for (let i = 0; i <= ws.length - n; i += 1) {
    const g = ws.slice(i, i + n).join(' ');
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return counts;
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let shared = 0;
  a.forEach((g) => {
    if (b.has(g)) shared += 1;
  });
  return (shared * 100) / (a.size + b.size - shared);
}

/* ---------- the checks ---------- */

export function checkPieceSeo(html: string, options: SeoOptions = {}): SeoResult {
  const t = { ...SEO_DEFAULTS, ...(options.thresholds ?? {}) };
  const ex = extract(html);
  const bodyWords = words(ex.body);
  const bodyStems = bodyWords.map(stem);
  const checks: SeoCheck[] = [];
  const add = (c: SeoCheck) => checks.push(c);
  const band = (ok: boolean, warn: boolean): SeoStatus => (ok ? 'pass' : warn ? 'warn' : 'fail');

  /* words */
  const wc = bodyWords.length;
  add({
    id: 'words',
    label: 'Word count',
    status: wc < t.minWords ? 'fail' : wc > t.maxWords ? 'warn' : 'pass',
    detail: wc.toLocaleString(),
    hint:
      wc < t.minWords
        ? `Below the ${t.minWords.toLocaleString()} floor for a long-form piece.`
        : wc > t.maxWords
          ? `Over ${t.maxWords.toLocaleString()} — check it is not padded.`
          : undefined,
  });

  /* primary phrase */
  const primary = options.primary?.trim();
  if (primary) {
    const { hits, density } = phraseCount(bodyStems, primary);
    add({
      id: 'density',
      label: 'Primary density',
      status:
        hits === 0 || density > t.ceiling
          ? 'fail'
          : density > t.maxDensity || density < t.minDensity
            ? 'warn'
            : 'pass',
      detail: `${hits}× · ${density.toFixed(2)}%`,
      hint:
        hits === 0
          ? 'The primary phrase never appears in the body.'
          : density > t.ceiling
            ? `Over the ${t.ceiling}% ceiling. Cut repetitions — do not rephrase around it.`
            : density > t.maxDensity
              ? `Above the ${t.minDensity}–${t.maxDensity}% target band.`
              : density < t.minDensity
                ? `Below ${t.minDensity}% — under-targeted.`
                : undefined,
    });

    const inH1 = ex.headings.some((h) => h.level === 1 && phraseCount(stems(h.text), primary).hits > 0);
    const opener = bodyStems.slice(0, 100);
    const inOpen = phraseCount(opener, primary).hits > 0;
    const inH2 = ex.headings.filter((h) => h.level === 2 && phraseCount(stems(h.text), primary).hits > 0).length;
    const missing = [!inH1 && 'H1', !inOpen && 'the first 100 words', !inH2 && 'any H2'].filter(
      Boolean,
    ) as string[];
    add({
      id: 'placement',
      label: 'Primary placement',
      status: missing.length ? 'warn' : 'pass',
      detail: missing.length ? `Missing from ${missing.join(', ')}` : `H1, opener, ${inH2} H2${inH2 === 1 ? '' : 's'}`,
      hint: missing.length ? `Present in ${inH2} H2${inH2 === 1 ? '' : 's'}.` : undefined,
    });
  }

  /* secondary phrases */
  const secondary = (options.secondary ?? []).filter((s) => s.trim());
  if (secondary.length) {
    const missing = secondary.filter((s) => phraseCount(bodyStems, s).hits === 0);
    add({
      id: 'secondary',
      label: 'Secondary phrases',
      status: missing.length ? 'warn' : 'pass',
      detail: `${secondary.length - missing.length}/${secondary.length} used`,
      hint: missing.length ? `Missing: ${missing.map((m) => `“${m}”`).join(', ')}` : undefined,
    });
  }

  /* headings */
  const h1s = ex.headings.filter((h) => h.level === 1);
  add({
    id: 'h1',
    label: 'H1',
    status: h1s.length === 1 ? 'pass' : 'fail',
    detail: h1s.length === 1 ? `“${h1s[0].text}”` : h1s.length === 0 ? 'None' : `${h1s.length} found`,
    hint: h1s.length === 1 ? undefined : 'An article needs exactly one H1.',
  });

  const skips: string[] = [];
  for (let i = 1; i < ex.headings.length; i += 1) {
    const prev = ex.headings[i - 1];
    const cur = ex.headings[i];
    if (cur.level - prev.level > 1) skips.push(`H${prev.level}→H${cur.level} at “${cur.text.slice(0, 40)}”`);
  }
  add({
    id: 'hierarchy',
    label: 'Heading hierarchy',
    status: skips.length ? 'fail' : 'pass',
    detail: skips.length ? `${skips.length} level skip${skips.length === 1 ? '' : 's'}` : `${ex.headings.length} headings, no skips`,
    hint: skips.length ? skips.slice(0, 3).join('; ') : undefined,
  });

  const h2s = ex.headings.filter((h) => h.level === 2);
  add({
    id: 'h2count',
    label: 'H2 count',
    status: h2s.length < 3 ? 'warn' : 'pass',
    detail: String(h2s.length),
    hint: h2s.length < 3 ? 'Long-form pieces usually want five or more scannable H2s.' : undefined,
  });

  const subs = ex.headings.filter((h) => h.level >= 2);
  if (subs.length) {
    const qs = subs.filter((h) => h.text.trim().endsWith('?'));
    const share = (qs.length * 100) / subs.length;
    add({
      id: 'questions',
      label: 'Question headings',
      status: share > t.maxQuestionHeadings || qs.length === 0 ? 'warn' : 'pass',
      detail: `${qs.length}/${subs.length} (${share.toFixed(0)}%)`,
      hint:
        share > t.maxQuestionHeadings
          ? `Over ${t.maxQuestionHeadings}% — mix in declarative headings that state the answer.`
          : qs.length === 0
            ? 'One or two question headings pick up long-tail queries.'
            : undefined,
    });
  }

  /* images */
  if (!ex.images.length) {
    add({ id: 'images', label: 'Images', status: 'warn', detail: 'None', hint: 'No images in the article body.' });
  } else {
    const weak = ex.images.filter(
      (i) =>
        words(i.alt).length < t.minAltWords ||
        ['image', 'photo', 'picture', 'alt', 'img'].includes(i.alt.trim().toLowerCase()),
    );
    add({
      id: 'alt',
      label: 'Image alt text',
      status: weak.length ? 'fail' : 'pass',
      detail: weak.length ? `${weak.length}/${ex.images.length} weak or missing` : `${ex.images.length}/${ex.images.length} descriptive`,
      hint: weak.length ? weak.slice(0, 3).map((w) => w.src.split('/').pop() || w.src).join('; ') : undefined,
    });
  }

  /* links */
  const internal = ex.links.filter(
    (l) => l.href && (!/^https?:/i.test(l.href) || (options.domain ? l.href.includes(options.domain) : false)),
  );
  const external = ex.links.filter((l) => /^https?:/i.test(l.href) && !internal.includes(l));
  add({
    id: 'internal',
    label: 'Internal links',
    status: internal.length < t.minInternalLinks ? 'warn' : 'pass',
    detail: `${internal.length} internal, ${external.length} external`,
    hint: internal.length < t.minInternalLinks ? `Want at least ${t.minInternalLinks}.` : undefined,
  });

  const lazy = ex.links.filter((l) =>
    ['click here', 'here', 'read more', 'learn more', 'this', 'link', 'more'].includes(
      l.text.trim().toLowerCase().replace(/\.$/, ''),
    ),
  );
  add({
    id: 'anchors',
    label: 'Anchor text',
    status: lazy.length ? 'fail' : 'pass',
    detail: lazy.length ? `${lazy.length} generic` : 'All descriptive',
    hint: lazy.length ? lazy.slice(0, 4).map((l) => `“${l.text}”`).join('; ') : undefined,
  });

  /* meta */
  if (options.metaTitle) {
    const n = options.metaTitle.length;
    add({
      id: 'metaTitle',
      label: 'Meta title',
      status: band(n >= 30 && n <= 60, true),
      detail: `${n} chars`,
      hint: n >= 30 && n <= 60 ? undefined : 'Aim for 30–60 to avoid truncation in results.',
    });
  }
  if (options.metaDescription !== undefined) {
    const n = options.metaDescription?.length ?? 0;
    add({
      id: 'metaDescription',
      label: 'Meta description',
      status: n >= 120 && n <= 160 ? 'pass' : 'warn',
      detail: n ? `${n} chars` : 'Absent',
      hint: n >= 120 && n <= 160 ? undefined : 'Aim for 120–160.',
    });
  }

  /* prose mechanics */
  const bangs = (ex.body.match(/!/g) ?? []).length;
  add({
    id: 'exclamations',
    label: 'Exclamation points',
    status: bangs > t.maxExclamations ? 'fail' : 'pass',
    detail: String(bangs),
    hint: bangs > t.maxExclamations ? `The ceiling is ${t.maxExclamations}.` : undefined,
  });

  const ss = sentences(ex.body);
  if (ss.length) {
    const avg = ss.reduce((n, s) => n + words(s).length, 0) / ss.length;
    const longs = ss.filter((s) => words(s).length > 40).length;
    add({
      id: 'sentences',
      label: 'Sentence length',
      status: avg > t.maxAvgSentence ? 'warn' : 'pass',
      detail: `avg ${avg.toFixed(1)} words, ${longs} over 40`,
      hint: avg > t.maxAvgSentence ? `Over ${t.maxAvgSentence} — break up the long ones.` : undefined,
    });
  }

  const fat = ex.paragraphs.filter((p) => words(p).length > t.maxParagraph);
  add({
    id: 'paragraphs',
    label: 'Paragraph length',
    status: fat.length ? 'warn' : 'pass',
    detail: fat.length
      ? `${fat.length} over ${t.maxParagraph} words`
      : `${ex.paragraphs.length} blocks, none over ${t.maxParagraph} words`,
    hint: fat.length ? 'Split them; long-form still needs scannable blocks.' : undefined,
  });

  const f = flesch(ex.body);
  if (f !== null) {
    const readable = f >= 45 && f <= 75;
    add({
      id: 'readability',
      label: 'Readability',
      status: readable ? 'pass' : 'warn',
      detail: `Flesch ${f.toFixed(0)} (${f < 45 ? 'hard' : f <= 70 ? 'plain' : 'simple'})`,
      hint: readable ? undefined : 'Consumer-facing copy reads best between 50 and 70.',
    });
  }

  /* repeated phrasing */
  const primaryStem = primary ? words(primary).map(stem).join(' ') : '';
  const tics = [...ngrams(bodyStems, 5).entries()]
    .filter(([g, c]) => c >= t.ticFloor && (!primaryStem || !g.includes(primaryStem)))
    .sort((a, b) => b[1] - a[1]);
  add({
    id: 'tics',
    label: 'Repeated phrasing',
    status: tics.length ? 'warn' : 'pass',
    detail: tics.length ? `${tics.length} phrase${tics.length === 1 ? '' : 's'} ${t.ticFloor}×+` : `No 5-word phrase ${t.ticFloor}×+`,
    hint: tics.length ? tics.slice(0, 3).map(([g, c]) => `“${g}” ${c}×`).join('; ') : undefined,
  });

  /* off-voice vocabulary */
  const offVoice = (options.offVoice ?? []).filter((s) => s.trim());
  if (offVoice.length) {
    const found = offVoice
      .map((term) => ({ term, hits: phraseCount(bodyStems, term).hits }))
      .filter((f2) => f2.hits > 0);
    add({
      id: 'offVoice',
      label: 'Off-voice terms',
      status: found.length ? 'warn' : 'pass',
      detail: found.length ? found.map((f2) => `“${f2.term}” ${f2.hits}×`).join('; ') : `0 of ${offVoice.length} present`,
      hint: found.length
        ? 'Fine when naming a competing method. A problem only if it describes our own service — read the sentence.'
        : undefined,
    });
  }

  /* near-duplicate against the client's other pieces */
  const library = options.library ?? [];
  if (library.length) {
    const mine = new Set(ngrams(bodyStems, 3).keys());
    let best: { title: string; score: number } | null = null;
    library.forEach((other) => {
      const theirs = new Set(ngrams(words(extract(other.html).body).map(stem), 3).keys());
      const score = jaccard(mine, theirs);
      if (!best || score > best.score) best = { title: other.title, score };
    });
    if (best) {
      const top = best as { title: string; score: number };
      add({
        id: 'overlap',
        label: 'Library overlap',
        status: top.score > t.maxOverlap ? 'fail' : 'pass',
        detail: `${top.score.toFixed(1)}% vs “${top.title}”`,
        hint: top.score > t.maxOverlap ? `Over ${t.maxOverlap}% — too close to an existing piece.` : undefined,
      });
    }
  }

  const counts: Record<SeoStatus, number> = { pass: 0, warn: 0, fail: 0 };
  checks.forEach((c) => {
    counts[c.status] += 1;
  });
  return { checks, counts, words: wc };
}
